/**
 * UserMenu Component
 *
 * Dropdown menu for authenticated users.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface User {
  displayName?: string;
  firstName?: string;
  photoURL?: string;
  role?: 'guest' | 'host' | 'admin';
}

interface UserMenuProps {
  user: User;
  unreadMessages?: number;
  onLogout: () => void;
}

export const UserMenu: React.FC<UserMenuProps> = ({
  user,
  unreadMessages = 0,
  onLogout,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayName = user.displayName || user.firstName || 'User';
  const initials = displayName.charAt(0).toUpperCase();
  const isHost = user.role === 'host' || user.role === 'admin';

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-1 border border-gray-300 rounded-full hover:shadow-md transition-shadow"
      >
        <MenuIcon className="w-5 h-5 ml-2 text-gray-500" />
        <div className="relative">
          {user.photoURL ? (
            <img
              src={user.photoURL}
              alt={displayName}
              className="w-8 h-8 rounded-full object-cover"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
              <span className="text-white text-sm font-medium">{initials}</span>
            </div>
          )}
          {unreadMessages > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
              {unreadMessages > 9 ? '9+' : unreadMessages}
            </span>
          )}
        </div>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
          <div className="px-4 py-2 border-b border-gray-100">
            <p className="font-medium text-gray-900">{displayName}</p>
            <p className="text-sm text-gray-500 capitalize">{user.role}</p>
          </div>

          <div className="py-1">
            <MenuLink to="/dashboard">Dashboard</MenuLink>
            <MenuLink to="/dashboard/bookings">
              My Bookings
            </MenuLink>
            <MenuLink to="/dashboard/messages" badge={unreadMessages}>
              Messages
            </MenuLink>
            <MenuLink to="/dashboard/saved">Saved Properties</MenuLink>
          </div>

          {isHost && (
            <>
              <div className="border-t border-gray-100 my-1" />
              <div className="py-1">
                <MenuLink to="/host/listings">My Listings</MenuLink>
                <MenuLink to="/host/earnings">Wallet</MenuLink>
                <MenuLink to="/host/calendar">Calendar</MenuLink>
              </div>
            </>
          )}

          <div className="border-t border-gray-100 my-1" />
          <div className="py-1">
            <MenuLink to="/dashboard/profile">Account Settings</MenuLink>
            <button
              onClick={() => {
                setIsOpen(false);
                onLogout();
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
            >
              Log out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

interface MenuLinkProps {
  to: string;
  children: React.ReactNode;
  badge?: number;
}

const MenuLink: React.FC<MenuLinkProps> = ({ to, children, badge }) => (
  <Link
    to={to}
    className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
  >
    <span>{children}</span>
    {badge !== undefined && badge > 0 && (
      <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
        {badge > 9 ? '9+' : badge}
      </span>
    )}
  </Link>
);

const MenuIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 6h16M4 12h16M4 18h16"
    />
  </svg>
);

export default UserMenu;
